import FirebaseNewImproved from './FirebaseNewImproved';
import { ref, query, orderByChild, equalTo } from 'firebase/database';

class FirebaseNewAdapter {
  constructor() {
    this.firebaseImproved = new FirebaseNewImproved();
    // expose underlying properties for compatibility
    this.auth = this.firebaseImproved.auth;
    this.db = this.firebaseImproved.db;
    this.storage = this.firebaseImproved.storage;

    // copy instance methods (class fields / arrow functions) from improved instance
    const ownNames = Object.getOwnPropertyNames(this.firebaseImproved);
    // reserve names that have adapter-level wrappers so we don't overwrite them
    const reserved = ['patchUser','getUser','insertTeam','patchTeam','getEvent','patchEvent','insertEvent','getTeamsMap','getEventsByCompetition','Team','Event','listenTeamsMap','listenUsersMap','listenEventsMap','listenScoresheetsMap','listenLiabilityMap'];
    for (const name of ownNames) {
      if (reserved.includes(name)) continue;
      if (typeof this.firebaseImproved[name] === 'function' && !(name in this)) {
        this[name] = this.firebaseImproved[name].bind(this.firebaseImproved);
      }
    }

    // ensure critical methods are available directly as instance properties
    const critical = ['patchUser','getUser','insertTeam','patchTeam','getEvent','patchEvent','insertEvent','getTeamsMap','getEventsByCompetition','Team','Event'];
    for (const method of critical) {
      if (!(method in this) && typeof this.firebaseImproved[method] === 'function') {
        this[method] = this.firebaseImproved[method].bind(this.firebaseImproved);
      }
    }
  }

  // Provide a compatibility wrapper for patchEvent as well (accept both signatures)
  async patchEvent(eventKeyOrCompound, eventObj, competition) {
    if (typeof this.firebaseImproved.patchEvent !== 'function') {
      throw new Error('patchEvent not implemented in FirebaseNewImproved');
    }
    let eventKey = eventKeyOrCompound;
    let comp = competition;
    if (!competition && typeof eventKeyOrCompound === 'string' && eventKeyOrCompound.includes('/')) {
      const parts = eventKeyOrCompound.split('/');
      comp = parts[0];
      eventKey = parts.slice(1).join('/');
    }
    if (!comp) {
      console.warn('patchEvent called without competition; proceeding with undefined competition');
    }
    return this.firebaseImproved.patchEvent(eventKey, eventObj, comp);
  }


  listenTeamUsersMap() {
    const db = this.firebaseImproved.db;
    return query(ref(db, 'users'), orderByChild('role'), equalTo('TEAM'));
  }

  listenTeamsMap() {
    const db = this.firebaseImproved.db;
    return ref(db, 'teamsNew');
  }

  listenEventsMap() {
    const db = this.firebaseImproved.db;
    return ref(db, 'eventsNew');
  }

  listenUsersMap() {
    const db = this.firebaseImproved.db;
    return ref(db, 'users');
  }

  listenJudgesMap() {
    const db = this.firebaseImproved.db;
    // match original behavior: query users where role === JUDGE
    return query(ref(db, 'users'), orderByChild('role'), equalTo('JUDGE'));
  }

  listenAdminUsersMap() {
    const db = this.firebaseImproved.db;
    return query(ref(db, 'users'), orderByChild('role'), equalTo('ADMIN'));
  }

  listenScoreCommitteeUsersMap() {
    const db = this.firebaseImproved.db;
    return query(ref(db, 'users'), orderByChild('role'), equalTo('SCORECOMMITTEE'));
  }

  listenUser(uid) {
    const db = this.firebaseImproved.db;
    return ref(db, `users/${uid}`);
  }

  listenGlobalSettings() {
    const db = this.firebaseImproved.db;
    return ref(db, 'globalSettings');
  }

  listenScoresheetsMap() {
    const db = this.firebaseImproved.db;
    return ref(db, 'scoresheets');
  }

  // Backwards-compatible listener for scores map
  listenScoresMap() {
    if (typeof this.firebaseImproved.listenScoresMap === 'function') {
      return this.firebaseImproved.listenScoresMap();
    }
    const db = this.firebaseImproved.db;
    // older DB path used for scores may be 'scores' or 'scoresMap' depending on implementation
    return ref(db, 'scores');
  }

  listenLiabilityMap() {
    const db = this.firebaseImproved.db;
    return ref(db, 'liabilityForm');
  }

  onAuthStateChanged(callback) {
    return this.firebaseImproved.onAuthStateChanged(callback);
  }

  // Expose getPercentageScoresDone if available; otherwise return null as a safe fallback
  async getPercentageScoresDone(competition) {
    if (typeof this.firebaseImproved.getPercentageScoresDone === 'function') {
      return this.firebaseImproved.getPercentageScoresDone(competition);
    }
    console.warn('getPercentageScoresDone not implemented in FirebaseNewImproved; returning null');
    return null;
  }

  // Provide insertGlobalScoresheet wrapper so callers on the adapter can use it
  async insertGlobalScoresheet(values, competition) {
    if (typeof this.firebaseImproved.insertGlobalScoresheet === 'function') {
      return this.firebaseImproved.insertGlobalScoresheet(values, competition);
    }
    throw new Error('insertGlobalScoresheet not implemented in FirebaseNewImproved');
  }

  // Explicit wrapper for patchUser to avoid any binding/proxy issues
  async insertAppeal(appeal, competition) {
    if (typeof this.firebaseImproved.insertAppeal === 'function') {
      return this.firebaseImproved.insertAppeal(appeal, competition);
    }
    throw new Error('insertAppeal not implemented in FirebaseNewImproved');
  }

  async getAppeal(appealKeyOrCompound, competition) {
    if (typeof this.firebaseImproved.getAppeal === 'function') {
      return this.firebaseImproved.getAppeal(appealKeyOrCompound, competition);
    }
    throw new Error('getAppeal not implemented in FirebaseNewImproved');
  }

  async getAppealsByCompetition(competition) {
    if (typeof this.firebaseImproved.getAppealsByCompetition === 'function') {
      return this.firebaseImproved.getAppealsByCompetition(competition);
    }
    throw new Error('getAppealsByCompetition not implemented in FirebaseNewImproved');
  }

  async getAppealsForTeam(teamUid, competition) {
    if (typeof this.firebaseImproved.getAppealsForTeam === 'function') {
      return this.firebaseImproved.getAppealsForTeam(teamUid, competition);
    }
    throw new Error('getAppealsForTeam not implemented in FirebaseNewImproved');
  }

  async getAppealsForEvent(eventUid, competition) {
    if (typeof this.firebaseImproved.getAppealsForEvent === 'function') {
      return this.firebaseImproved.getAppealsForEvent(eventUid, competition);
    }
    throw new Error('getAppealsForEvent not implemented in FirebaseNewImproved');
  }

  async patchAppeal(appealKeyOrCompound, newAppeal, competition) {
    if (typeof this.firebaseImproved.patchAppeal === 'function') {
      return this.firebaseImproved.patchAppeal(appealKeyOrCompound, newAppeal, competition);
    }
    throw new Error('patchAppeal not implemented in FirebaseNewImproved');
  }

  async resolveAppeal(appealKeyOrCompound, resolution, competition) {
    if (typeof this.firebaseImproved.resolveAppeal === 'function') {
      return this.firebaseImproved.resolveAppeal(appealKeyOrCompound, resolution, competition);
    }
    throw new Error('resolveAppeal not implemented in FirebaseNewImproved');
  }

  async reopenAppeal(appealKeyOrCompound, competition) {
    if (typeof this.firebaseImproved.reopenAppeal === 'function') {
      return this.firebaseImproved.reopenAppeal(appealKeyOrCompound, competition);
    }
    throw new Error('reopenAppeal not implemented in FirebaseNewImproved');
  }

  async deleteAppeal(appealKeyOrCompound, competition) {
    if (typeof this.firebaseImproved.deleteAppeal === 'function') {
      return this.firebaseImproved.deleteAppeal(appealKeyOrCompound, competition);
    }
    throw new Error('deleteAppeal not implemented in FirebaseNewImproved');
  }

  listenAppealsMap() {
    const db = this.firebaseImproved.db;
    return ref(db, 'appealsNew');
  }

  async storageUploadAppealEvidence(file, fileName, competition, appealId) {
    if (typeof this.firebaseImproved.storageUploadAppealEvidence === 'function') {
      return this.firebaseImproved.storageUploadAppealEvidence(file, fileName, competition, appealId);
    }
    throw new Error('storageUploadAppealEvidence not implemented in FirebaseNewImproved');
  }

  async storageDeleteAppealEvidence(competition, appealId) {
    if (typeof this.firebaseImproved.storageDeleteAppealEvidence === 'function') {
      return this.firebaseImproved.storageDeleteAppealEvidence(competition, appealId);
    }
    throw new Error('storageDeleteAppealEvidence not implemented in FirebaseNewImproved');
  }

  // Explicit wrapper for patchUser to avoid any binding/proxy issues
  async patchUser(userKey, newUser, competition) {
    if (typeof this.firebaseImproved.patchUser === 'function') {
      return this.firebaseImproved.patchUser(userKey, newUser, competition);
    }
    throw new Error('patchUser not implemented in FirebaseNewImproved');
  }

  // Explicit wrapper for getUser
  async getUser(uid) {
    if (typeof this.firebaseImproved.getUser === 'function') {
      return this.firebaseImproved.getUser(uid);
    }
    throw new Error('getUser not implemented in FirebaseNewImproved');
  }

  // Explicit wrapper for insertTeam
  async insertTeam(team, competition) {
    if (typeof this.firebaseImproved.insertTeam === 'function') {
      return this.firebaseImproved.insertTeam(team, competition);
    }
    throw new Error('insertTeam not implemented in FirebaseNewImproved');
  }

  // Explicit wrapper for patchTeam (accepts either (teamKey, team, competition) or ("competition/teamKey", team))
  async patchTeam(teamKeyOrCompound, team, competition) {
    if (typeof this.firebaseImproved.patchTeam !== 'function') {
      throw new Error('patchTeam not implemented in FirebaseNewImproved');
    }
    // Support old callers that pass "competition/teamKey" as first arg and no competition param
    let teamKey = teamKeyOrCompound;
    let comp = competition;
    if (!competition && typeof teamKeyOrCompound === 'string' && teamKeyOrCompound.includes('/')) {
      const parts = teamKeyOrCompound.split('/');
      comp = parts[0];
      teamKey = parts.slice(1).join('/');
    }
    // If still no comp, try to find teamKey format like "competition/team" or use provided comp
    if (!comp) {
      console.warn('patchTeam called without competition; proceeding with undefined competition');
    }
    return this.firebaseImproved.patchTeam(teamKey, team, comp);
  }

  // Presentation music wrappers
  async getPresentationMusicList(competition) {
    if (typeof this.firebaseImproved.getPresentationMusicList === 'function') {
      return this.firebaseImproved.getPresentationMusicList(competition);
    }
    throw new Error('getPresentationMusicList not implemented in FirebaseNewImproved');
  }

  async getPresentationMusicUrl(fileName, competition) {
    if (typeof this.firebaseImproved.getPresentationMusicUrl === 'function') {
      return this.firebaseImproved.getPresentationMusicUrl(fileName, competition);
    }
    throw new Error('getPresentationMusicUrl not implemented in FirebaseNewImproved');
  }

  async uploadPresentationMusic(file, competition) {
    if (typeof this.firebaseImproved.uploadPresentationMusic === 'function') {
      return this.firebaseImproved.uploadPresentationMusic(file, competition);
    }
    throw new Error('uploadPresentationMusic not implemented in FirebaseNewImproved');
  }

   async deletePresentationMusic(fileName, competition) {
    if (typeof this.firebaseImproved.deletePresentationMusic === 'function') {
      return this.firebaseImproved.deletePresentationMusic(fileName, competition);
    }
    throw new Error('deletePresentationMusic not implemented in FirebaseNewImproved');
  }

  // Explicit wrapper for deleteCompetitionComplex
  async deleteCompetitionComplex(competitionKey) {
    if (typeof this.firebaseImproved.deleteCompetitionComplex === 'function') {
      return this.firebaseImproved.deleteCompetitionComplex(competitionKey);
    }
    throw new Error('deleteCompetitionComplex not implemented in FirebaseNewImproved');
  }

  // Explicit wrapper for patchTeam (accepts either (teamKey, team, competition) or ("competition/teamKey", team))
  async patchTeam(teamKeyOrCompound, team, competition) {
    if (typeof this.firebaseImproved.patchTeam !== 'function') {
      throw new Error('patchTeam not implemented in FirebaseNewImproved');
    }
    // Support old callers that pass "competition/teamKey" as first arg and no competition param
    let teamKey = teamKeyOrCompound;
    let comp = competition;
    if (!competition && typeof teamKeyOrCompound === 'string' && teamKeyOrCompound.includes('/')) {
      const parts = teamKeyOrCompound.split('/');
      comp = parts[0];
      teamKey = parts.slice(1).join('/');
    }
    // If still no comp, try to find teamKey format like "competition/team" or use provided comp
    if (!comp) {
      console.warn('patchTeam called without competition; proceeding with undefined competition');
    }
    return this.firebaseImproved.patchTeam(teamKey, team, comp);
  }

}

// Proxy all other methods dynamically

const methodNames = Object.getOwnPropertyNames(FirebaseNewImproved.prototype).filter(name => name !== 'constructor');
for (const name of methodNames) {
  if (!(name in FirebaseNewAdapter.prototype)) {
    FirebaseNewAdapter.prototype[name] = function(...args) {
      return this.firebaseImproved[name](...args);
    };
  }
}

export default FirebaseNewAdapter;
